import { Dtos, SuretyQuoteStatus } from "@inrev/common";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { useReportLoadError } from "@portal/components/features/load-errors";
import { cn } from "@portal/components/lib/utils";
import { unableToLoadMessage } from "@portal/components/patterns";
import { ClickableText, Icon, PartialIndicator } from "@portal/components/primitives";
import { RequiredForApprovalIndicator } from "@portal/components/ui/RequiredForApprovalIndicator";
import { type FC, Fragment, type ReactNode } from "react";
import { useAdminBondRequestNotices } from "../../shared/useAdminBondRequestNotices";

type RetryableLoad = {
	error: Error | null;
	onRetry: () => void;
	retrying: boolean;
};

export type AdminBondRequestHeaderNoticesProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response | undefined;
	accountApproved: boolean;
	isComplete: boolean;
	underwriting?: RetryableLoad;
};

type NoticeRow = {
	key: string;
	icon: ReactNode;
	text: string;
	muted?: boolean;
	action?: ReactNode;
};

export const AdminBondRequestHeaderNotices: FC<AdminBondRequestHeaderNoticesProps> = ({
	request,
	bondForm,
	accountApproved,
	isComplete,
	underwriting,
}) => {
	useReportLoadError(underwriting?.error);
	const notices = useAdminBondRequestNotices(request, bondForm);

	const rows: NoticeRow[] = [];

	if (underwriting?.error) {
		rows.push({
			key: "account-underwriting",
			icon: <Icon type="exclamation-triangle" size="small" color="warning" className="shrink-0" />,
			text: unableToLoadMessage("account underwriting", "this request can't be approved"),
			action: (
				<ClickableText
					type="body"
					color="link"
					onClick={underwriting.onRetry}
					disabled={underwriting.retrying}
				>
					{underwriting.retrying ? "Retrying…" : "Retry"}
				</ClickableText>
			),
		});
	}

	if (request.status === SuretyQuoteStatus.review) {
		if (!accountApproved && !underwriting?.error) {
			rows.push({
				key: "account-not-approved",
				icon: <PartialIndicator active />,
				text: "Cannot approve request until account is approved",
				muted: true,
			});
		}
		if (!isComplete) {
			rows.push({
				key: "missing-required-properties",
				icon: <RequiredForApprovalIndicator active className="h-[14px] w-[14px]" />,
				text: "Cannot approve request until all required properties are provided",
				muted: true,
			});
		}
	}

	for (const notice of notices) {
		rows.push({
			key: notice.id,
			icon:
				notice.type === "action" ? (
					<Icon type="exclamation-triangle" size="small" color="warning" className="shrink-0" />
				) : (
					<Icon type="clock" color="tertiary" size="small" className="shrink-0" />
				),
			text: notice.text,
		});
	}

	if (rows.length === 0) return null;

	return (
		<div
			data-testid="admin-bond-request-header-notices"
			className="grid grid-cols-[16px_1fr] items-start gap-x-2 gap-y-[6px]"
		>
			{rows.map(({ key, icon, text, muted, action }) => (
				<Fragment key={key}>
					<div className="flex h-[18px] items-center justify-center">{icon}</div>
					<div
						className={cn(
							"text-[13px] leading-[18px]",
							muted ? "text-tertiary italic" : "text-secondary",
						)}
					>
						{text}
						{action ? <span className="pl-[6px]">{action}</span> : null}
					</div>
				</Fragment>
			))}
		</div>
	);
};
